import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useRouteMatch } from "../../../utils/router";
import Select from "react-select";

import {
	getLink,
	formatCurrency,
	isVendedor,
	permisions,
} from "../../../utils/helpers";
import PageHeader from "../../general/PageHeader";
import Loader from "../../general/Loader";
import Pagination from "../../general/Pagination";
import { request } from "../../../utils/request";
import FiltersContainer from "../../general/FiltersContainer";
import { ESTADOS_COTIZACIONES } from "../../../utils/types";
import InvalidScreen from "../../general/InvalidScreen";
import { auth } from "../../../utils/auth";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";
import Dropdown from "../../general/Dropdown";
import { transform } from "lodash";

const formatDateTime = (date) => {
	return dayjs(date).format("DD/MM/YYYY hh:mm a");
};

const renderEstadoBadge = (estado) => {
	if (estado === "pendiente") {
		return <span className="badge badge-secondary p-2">Pendiente</span>;
	} else if (estado === "pagada") {
		return <span className="badge badge-success p-2">Pagada</span>;
	}else if (estado === "facturada") {
		return <span className="badge badge-success p-2">Facturada</span>;
	}

	return <span className="badge badge-danger p-2">Cancelada</span>;
};

const Cotizaciones = () => {
	// Route  matcher
	let match = useRouteMatch();

	const [filtros, setFiltros] = useState({
		buscar: "",
		vendedor: isVendedor() ? auth.getUser().empleado?.id : "",
		estado: "",
        limit: 15,
		desde: dayjs().format("YYYY-MM-DD"),
		hasta: dayjs().format("YYYY-MM-DD"),
	});

	// Derivado de `filtros` en vez de un estado que un efecto rellenaba después:
	// en el primer render valía "" y los enlaces de reportes salían sin filtros.
	const filtrosQuery = useMemo(
		() =>
			"?" +
			Object.keys(filtros)
				.map((fil) => `${fil}=${filtros[fil]}`)
				.join("&"),
		[filtros]
	);

	const [cotizaciones, setCotizaciones] = useState({});
	const [vendedores, setVendedores] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const getCotizaciones = (url) => {
		url = url ? url : "cotizaciones?limit=15";
		setIsLoading(true);

		request(url).then((res) => {
			setCotizaciones(res);
			setIsLoading(false);
		});
	};

	// El debounce se reconstruía en cada render, así que cada pulsación
	// estrenaba su propio temporizador y no agrupaba nada. Ahora el filtro entra
	// al estado de inmediato y lo que espera es la petición.
	const onFilterChange = (value, filtro) =>
		setFiltros((previos) => ({ ...previos, [filtro]: value }));

    const displayFilters = () => {
        document.querySelectorAll('.filtros-principales-movil').forEach((el) =>
            el.classList.toggle('d-none')
        );
    };

	const getVendedoresForDropdown = () => {
		request("vendedores?limit=100").then((res) => {
			const vendedores = res.data.filter((vendedor) => vendedor != null);
			setVendedores([
				{ value: "", label: "Todos" },
				...vendedores.map((vendedor) => ({
					value: vendedor.id,
					label: `${vendedor.nombre} ${vendedor.apellido}`,
				})),
			]);
			// setIsLoading(false);
		});
	};

	

	// saltarPrimero en false: el listado entra por aquí al montar, y esa primera
	// corrida no espera el debounce. Reemplaza al contador `calls`, que existía
	// sólo para saltarse la corrida en la que filtrosQuery todavía no estaba armado.
	useDebouncedEffect(() => {
		getCotizaciones(`cotizaciones${filtrosQuery}`);
	}, [filtrosQuery], { saltarPrimero: false });

	useEffect(() => {
		getVendedoresForDropdown();
	}, []);

	/*if (isVendedor() && !localStorage.getItem("rutas")) {
		return <InvalidScreen message="No hay zonas seleccionadas" />;
	}*/

	if (auth.is('vendedor-cliente') && !localStorage.getItem("almacenes")) {
		return <InvalidScreen message="No hay almacenes seleccionados" />;
	}

	return (
		<>
			<PageHeader title="Cotizaciones">
				<Link
					to={getLink(`/imprimir/cotizaciones${filtrosQuery}`)}
					target="_blank"
					className="au-btn-filter m-r-15 d-none d-lg-inline-block"
				>
					<i className="zmdi zmdi-print" /> Imprimir
				</Link>
				<Link
					className="au-btn au-btn-icon au-btn--green au-btn--small m-r-15"
					to={`${match.url}/crear`}
				>
					<i className="zmdi zmdi-plus" />
					Añadir Cotización
				</Link>
			</PageHeader>
			<FiltersContainer>
				<div className="col">
					<label>Buscar</label>
					<input
						onChange={(e) =>
							onFilterChange(e.target.value, "buscar")
						}
						type="search"
						placeholder="Buscar..."
						className="form-control"
						name="buscar"
					/>
				</div>
				<div className="col-md-2 filtros-principales-movil d-none d-lg-block">
					<label>Estado</label>
					<Select
						id="estado"
						options={[{value: "", label: "Todos"}, ...ESTADOS_COTIZACIONES]}
						name="estado"
						onChange={(e) => onFilterChange(e.value, "estado")}
						defaultValue={{ value: null, label: "Todos" }}
						styles={{
							control: (styles) => ({
								...styles,
								height: "38px",
							}),
						}}
					/>
				</div>
				{!isVendedor() && (<div className="col-md-2 filtros-principales-movil d-none d-lg-block">
					<label>Vendedor</label>
					<Select
						id="vendedor"
						options={vendedores}
						name="vendedor"
						onChange={(e) => onFilterChange(e.value, "vendedor")}
						isDisabled={auth.is('vendedor-cliente')}
						defaultValue={auth.is('vendedor-cliente') ? { value: auth.getUser().empleado?.id, label: `${auth.getUser().empleado?.nombre} ${auth.getUser().empleado?.apellido}` } : { value: null, label: "Todos" }}
						styles={{
							control: (styles) => ({
								...styles,
								height: "38px",
							}),
						}}
					/>
				</div>)}
				<div className="col-md-2">
					<label htmlFor="desde">Desde</label>
					<input
						type="date"
						name="desde"
						id="desde"
						defaultValue={dayjs()
							.startOf("day")
							.format("YYYY-MM-DD")}
						className="form-control"
						onChange={(e) =>
							onFilterChange(e.target.value, "desde")
						}
					/>
				</div>
				<div className="col-md-2">
					<label htmlFor="hasta">Hasta</label>
					<input
						type="date"
						name="hasta"
						id="hasta"
						defaultValue={dayjs().endOf("day").format("YYYY-MM-DD")}
						className="form-control"
						onChange={(e) =>
							onFilterChange(e.target.value, "hasta")
						}
					/>
				</div>
				<div style={{width: '50px'}} className="filtros-avanzados filtros-avanzados-movil d-lg-none" onClick={displayFilters}>
					<label>&nbsp;</label>
					<button type="button" className="btn" title="Filtros Avanzados">
						<i className="zmdi zmdi-filter-list"></i>
						<span className="d-lg-none">Más filtros</span>
					</button>
				</div>
			</FiltersContainer>

			{isLoading && <Loader />}
			{!isLoading && cotizaciones.data?.length ? (
				<div className="row px-3" style={{ overflowX: "auto", paddingBottom: "5rem" }}>
					<CotizacionesListDesktop cotizaciones={cotizaciones} />
			
					<CotizacionesListMobile cotizaciones={cotizaciones} />
			
					{cotizaciones.links && (
						<Pagination
							links={cotizaciones.links}
							meta={cotizaciones.meta}
							getData={getCotizaciones}
						/>
					)}
				</div>) : (
					<div>
						{!isLoading && (
							<div className="text-center mt-5 mb-5 w-100">No hay ninguna Cotización creada con los filtros aplicados.</div>
						)}
					</div>
				)}
		</>
	);
};

const CotizacionesListDesktop = ({cotizaciones}) => {
	return <div className="table-data table-responsive table-row__desktop" style={{ overflowX: "visible" }}>
		<table className="table table-data2">
			<thead>
				<tr>
					<th>NCF/Numero</th>
					<th>Cliente</th>
					<th>Creación</th>
					<th>Vencimiento</th>
					<th>Total</th>
					<th>Vendedor</th>
					<th>Zona</th>
					<th className="text-center">Estado</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{cotizaciones.data.map((cotizacion) => {
					return (
						<tr
							className="tr-shadow"
							key={cotizacion.id}
						>
							<td>
								{cotizacion.numero_comprobante}
							</td>
							<td>{cotizacion.cliente?.codigo} - {cotizacion.cliente?.nombre}</td>
							<td>
								{formatDateTime(
									cotizacion.created_at
								)}
							</td>
							<td>
								{dayjs(
									cotizacion.fecha_vencimiento
								).format("DD/MM/YYYY")}
							</td>
							<td>
								{formatCurrency(
									cotizacion.total,
									"$"
								)}
							</td>
							<td style={{lineHeight: 1}}>
								{cotizacion.vendedor && (
									`${cotizacion.vendedor?.nombre} ${cotizacion.vendedor?.apellido}`
								)}
								{cotizacion.usuario && (<div><small>Creada por: {cotizacion.usuario?.username}</small></div>)}
							</td>
							<td>{cotizacion.ruta?.nombre}</td>
							<td className="text-center">
								{renderEstadoBadge(
									cotizacion.estado
								)}
							</td>
							<td>
								<div className="dropdown">
									<button className="au-btn au-btn-icon au-btn-filter">
										Acciones
									</button>
									<div
										style={{
											left: "-30%",
										}}
										className="dropdown-menu"
									>
										{auth.can(permisions.reporteCotizaciones) && <Link
											to={getLink(
												`/imprimir/cotizaciones/${cotizacion.id}`
											)}
											target="_blank"
											className="dropdown-item"
											title="Imprimir"
										>
											Impresión
										</Link>}
										{cotizacion.estado === "pendiente" && (
											<Link
												className="dropdown-item"
												to={getLink(
													`/ingresos/facturas/crear/${cotizacion.id}`
												)}
												title="Facturar"
												data-original-title="Facturar"
												onClick={() => localStorage.setItem('fromOrden', JSON.stringify('no'))}
											>
												Facturar
											</Link>
										)}
										{cotizacion.estado ===
											"pendiente" && (
											<Link
												to={getLink(
													`/ingresos/cotizaciones/${cotizacion.id}/edit`
												)}
												className="dropdown-item"
												title="Editar"
											>
												Editar
											</Link>
										)}
									</div>
								</div>
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	</div>;
};

const CotizacionesListMobile = ({cotizaciones}) => {
	return <div className="table-row__mobile w-100" style={{ overflowX: "visible" }}>
        {cotizaciones.data.map((cotizacion) => {
			return <div className="col-12" key={cotizacion.id}>
				<fieldset className="fieldset px-3" style={{ border: "1px solid rgba(0,138,171,0.8)", margin: '0 0 20px' }}>
                    <legend>{cotizacion.numero_comprobante}</legend>

					<div className="row">
						<div className="form-group col-12 mb-1">
							<div>
								<strong>{cotizacion.cliente?.nombre}</strong>
							</div>
						</div>
					</div>

					<table style={{ width: '100%', tableLayout: 'fixed'}}>
						<tbody>
							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Creación</small></td>
								<td className="pb-1 text-right">{formatDateTime(cotizacion.created_at)}</td>
							</tr>

							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Vencimiento</small></td>
								<td className="pb-1 text-right">{dayjs(cotizacion.fecha_vencimiento).format("DD/MM/YYYY")}</td>
							</tr>

							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Vendedor</small></td>
								<td className="pb-1 text-right">{cotizacion.vendedor && (`${cotizacion.vendedor?.nombre} ${cotizacion.vendedor?.apellido}`)}</td>
							</tr>

							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Creada por</small></td>
								<td className="pb-1 text-right">{cotizacion.usuario && (cotizacion.usuario?.username)}</td>
							</tr>

							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Zona</small></td>
								<td className="pb-1 text-right">{cotizacion.ruta?.nombre}</td>
							</tr>

							<tr>
								<td className="pb-1" style={{width: "110px"}}><small className="orden-filter-label uppercase">Estado</small></td>
								<td className="pb-1 text-right">{renderEstadoBadge(cotizacion.estado)}</td>
							</tr>
						</tbody>
					</table>

					<div className="row">
						<div className="form-group col-7 pr-1 mb-1">
							<label className="orden-filter-label uppercase">
								Total
							</label>
							<input
								type="text"
								placeholder="Cliente"
								value={formatCurrency(cotizacion.total, "$")}
								style={{ backgroundColor: "#fff" }}
								className="form-control"
								readOnly
							/>
						</div>
						<div className="form-group col-5 pl-1 mb-1">
							<label>
								<br />
							</label>
							<Dropdown
								classes={{ button: "w-100" }}
								styles={{
									button: {
										height: "38px",
										lineHeight: "0px",
										border: "1px solid #FE9D1F",
									},
									dropdownMenu: {
										zIndex: "99",
										transform: "translateX(calc(-100% + 105px))"
									},
								}}
							>
								{auth.can(permisions.reporteCotizaciones) && <Link
									to={getLink(
										`/imprimir/cotizaciones/${cotizacion.id}`
									)}
									target="_blank"
									className="dropdown-item"
									title="Imprimir"
								>
									Impresión
								</Link>}

								{cotizacion.estado === "pendiente" && (
									<Link
										className="dropdown-item"
										to={getLink(
											`/ingresos/facturas/crear/${cotizacion.id}`
										)}
										title="Facturar"
										data-original-title="Facturar"
										onClick={() => localStorage.setItem('fromOrden', JSON.stringify('no'))}
									>
										Facturar
									</Link>
								)}
								
								{cotizacion.estado ===
									"pendiente" && (
									<Link
										to={getLink(
											`/ingresos/cotizaciones/${cotizacion.id}/edit`
										)}
										className="dropdown-item"
										title="Editar"
									>
										Editar
									</Link>
								)}

							</Dropdown>
						</div>
					</div>
				</fieldset>
			</div>
		})}
	</div>;
};

export default Cotizaciones;
